import type { Data, Layout } from 'plotly.js-dist-min';

export type BarGrouping = 'grouped' | 'stacked' | 'percent100';
export type Orientation = 'vertical' | 'horizontal';

/**
 * Applies bar grouping mode to the layout.
 * Mutates the layout object in place.
 */
export function applyBarGrouping(layout: Partial<Layout>, grouping: BarGrouping): void {
  if (!layout) return;

  switch (grouping) {
    case 'grouped':
      layout.barmode = 'group';
      delete (layout as any).barnorm;
      break;
    case 'stacked':
      layout.barmode = 'stack';
      delete (layout as any).barnorm;
      break;
    case 'percent100':
      layout.barmode = 'stack';
      layout.barnorm = 'percent';
      break;
  }
}

/**
 * Applies orientation to bar traces and swaps axes accordingly.
 * Mutates both data and layout objects in place.
 */
export function applyOrientation(data: Data[], layout: Partial<Layout>, orientation: Orientation): void {
  if (!data || !layout) return;

  const isHorizontal = orientation === 'horizontal';
  const targetOrientation = isHorizontal ? 'h' : 'v';

  // Determine current orientation from bar traces
  // If any bar trace has orientation='h', we're currently horizontal
  const barTraces = data.filter((t: any) => t.type === 'bar');
  if (barTraces.length === 0) return;

  const currentlyHorizontal = barTraces.some((t: any) => (t as any).orientation === 'h');
  const needsAxisSwap = currentlyHorizontal !== isHorizontal;

  // Process all bar traces
  for (const trace of barTraces) {
    const barTrace = trace as any;

    // Check if this trace's orientation needs to change
    const currentOrientation = barTrace.orientation;
    const needsTraceSwap = isHorizontal 
      ? currentOrientation !== 'h'
      : currentOrientation === 'h';

    if (needsTraceSwap) {
      // Swap x and y
      const tempX = barTrace.x;
      const tempY = barTrace.y;
      barTrace.x = tempY;
      barTrace.y = tempX;

      // Swap error_x and error_y if they exist
      if (barTrace.error_x || barTrace.error_y) {
        const tempErrorX = barTrace.error_x;
        const tempErrorY = barTrace.error_y;
        barTrace.error_x = tempErrorY;
        barTrace.error_y = tempErrorX;
      }

      // Set orientation
      barTrace.orientation = targetOrientation;
    } else if (currentOrientation === undefined && !isHorizontal) {
      // Ensure vertical bars have explicit orientation
      barTrace.orientation = 'v';
    }
  }

  // Swap axis configurations when orientation changes
  if (needsAxisSwap) {
    const tempXAxis = layout.xaxis;
    const tempYAxis = layout.yaxis;

    // Swap the axis objects
    layout.xaxis = tempYAxis;
    layout.yaxis = tempXAxis;

    // Also handle xaxis2, yaxis2, etc. if they exist
    if ((layout as any).xaxis2 || (layout as any).yaxis2) {
      const tempXAxis2 = (layout as any).xaxis2;
      const tempYAxis2 = (layout as any).yaxis2;
      (layout as any).xaxis2 = tempYAxis2;
      (layout as any).yaxis2 = tempXAxis2;
    }
  }
}

